import React, { useCallback, useRef, useState } from "react";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckIcon from "@mui/icons-material/Check";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import {
  DatasetLabel,
  createLabel,
  deleteLabel,
  listLabels,
  updateDataset,
} from "@/lib/api/datasets";
import { TooltipButton } from "@/components/common/TooltipButton";
import { DropdownMenu } from "@/components/common/DropdownMenu";
import { NumberCount } from "@/components/common/NumberCount";
import { TextInput } from "@/components/common/TextInput";
import { DatasetMetaSection, MetaAddButton } from "./DatasetMetaSection";
import { showNotificationGlobal } from "@/stores/useNotificationStore";

const MAX_LABEL_NAME_LEN = 60;

const byName = (a: DatasetLabel, b: DatasetLabel) =>
  a.name.localeCompare(b.name);

interface DatasetLabelsProps {
  datasetId: string;
  /// The dataset's currently-assigned labels (owned by the parent row).
  labels: DatasetLabel[];
  /// Assign/unassign labels on THIS dataset (dataset manage permission).
  canEdit: boolean;
  /// Create/delete labels in the shared catalog (catalog-manager only).
  canManageCatalog: boolean;
  /// Bubble the new assigned set up so the row keeps the latest copy.
  onLabelsChange: (labels: DatasetLabel[]) => void;
  pr?: string;
}

/// Dataset labels — assigned chips plus an inline picker over the manager-
/// managed global catalog. Editors assign/unassign; catalog managers can also
/// create and delete labels from the picker itself (no separate admin page).
/// The website surfaces + filters datasets by these labels.
export const DatasetLabels: React.FC<DatasetLabelsProps> = ({
  datasetId,
  labels,
  canEdit,
  canManageCatalog,
  onLabelsChange,
  pr,
}) => {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState<DatasetLabel[] | null>(null);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const addRef = useRef<HTMLDivElement>(null);

  const assignedIds = new Set(labels.map((l) => l.id));

  const loadCatalog = useCallback(async () => {
    try {
      setCatalog(await listLabels());
    } catch {
      showNotificationGlobal("Failed to load labels", "error");
    }
  }, []);

  const openMenu = useCallback(() => {
    setOpen(true);
    void loadCatalog();
  }, [loadCatalog]);

  // Persist a new assigned set (optimistic; roll back on failure).
  const persist = useCallback(
    async (next: DatasetLabel[]) => {
      const prev = labels;
      onLabelsChange(next);
      try {
        await updateDataset(datasetId, { label_ids: next.map((l) => l.id) });
      } catch {
        onLabelsChange(prev);
        showNotificationGlobal("Failed to update labels", "error");
      }
    },
    [datasetId, labels, onLabelsChange],
  );

  const toggle = useCallback(
    (label: DatasetLabel) => {
      if (assignedIds.has(label.id)) {
        void persist(labels.filter((l) => l.id !== label.id));
      } else {
        void persist([...labels, label].sort(byName));
      }
    },
    [assignedIds, labels, persist],
  );

  const create = useCallback(async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const label = await createLabel(name);
      setNewName("");
      setCatalog((c) => (c ? [...c, label].sort(byName) : [label]));
      void persist([...labels, label].sort(byName)); // auto-assign the new label
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response
        ?.status;
      showNotificationGlobal(
        status === 409 ? "That label already exists" : "Failed to create label",
        "error",
      );
    } finally {
      setBusy(false);
    }
  }, [newName, busy, labels, persist]);

  const removeFromCatalog = useCallback(
    async (label: DatasetLabel) => {
      try {
        await deleteLabel(label.id);
        setCatalog((c) => c?.filter((l) => l.id !== label.id) ?? null);
        // A deleted label cascades off every dataset — reflect it locally too.
        if (assignedIds.has(label.id)) {
          onLabelsChange(labels.filter((l) => l.id !== label.id));
        }
      } catch {
        showNotificationGlobal("Failed to delete label", "error");
      }
    },
    [assignedIds, labels, onLabelsChange],
  );

  // A viewer of a dataset with no labels sees nothing.
  if (!canEdit && labels.length === 0) return null;

  return (
    <>
      <DatasetMetaSection
        icon={<LabelOutlinedIcon className="icon-sm" />}
        label="Labels"
        pr={pr}
        collapsible
        defaultCollapsed
        itemCount={labels.length}
        meta={
          labels.length > 0 ? (
            <NumberCount count={labels.length} size="sm" />
          ) : undefined
        }
        action={
          canEdit ? (
            <div ref={addRef}>
              <MetaAddButton
                label="Add label"
                onClick={open ? () => setOpen(false) : openMenu}
              />
            </div>
          ) : undefined
        }
      >
        {labels.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            {labels.map((l) => (
              <div
                key={l.id}
                className="group/chip inline-flex items-center max-w-[320px] min-h-(--control-height-sm) px-2 py-0.5 rounded-app border border-border text-fg select-none hover:border-accent transition-colors"
              >
                <span
                  className="truncate"
                  style={{
                    fontSize: "var(--font-size-small)",
                    lineHeight: 1.2,
                  }}
                >
                  {l.name}
                </span>
                {canEdit && (
                  <span className="inline-flex shrink-0 overflow-hidden opacity-0 max-w-0 group-hover/chip:opacity-100 group-hover/chip:max-w-7 group-hover/chip:ml-0.5 transition-all duration-200">
                    <TooltipButton
                      title="Remove"
                      onClick={() => toggle(l)}
                      variant="default"
                      size="xs"
                    >
                      <CloseOutlinedIcon />
                    </TooltipButton>
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : null}
      </DatasetMetaSection>

      <DropdownMenu
        anchorRef={addRef}
        isOpen={open}
        onClose={() => setOpen(false)}
        placement="bottom-end"
        style={{ width: "18rem" }}
      >
        <div className="max-h-72 overflow-y-auto p-1">
          {catalog === null ? (
            <div className="px-2 py-1.5 text-small text-fg-muted">Loading…</div>
          ) : catalog.length === 0 ? (
            <div className="px-2 py-1.5 text-small text-fg-muted">
              No labels yet
            </div>
          ) : (
            catalog.map((l) => {
              const assigned = assignedIds.has(l.id);
              return (
                <div
                  key={l.id}
                  className="flex items-center rounded-app group/lbl hover:bg-bg-hover"
                >
                  <button
                    type="button"
                    onClick={() => toggle(l)}
                    className="flex-1 flex items-center gap-2 px-2 py-1.5 text-left text-small cursor-pointer"
                  >
                    <span
                      className={`flex items-center justify-center size-4 shrink-0 rounded-sm border transition-colors ${
                        assigned
                          ? "bg-accent-15 border-accent"
                          : "bg-transparent border-border"
                      }`}
                    >
                      {assigned && (
                        <CheckIcon
                          sx={{ fontSize: 12, color: "var(--color-accent)" }}
                        />
                      )}
                    </span>
                    {l.name}
                  </button>
                  {canManageCatalog && (
                    <TooltipButton
                      title="Delete label from catalog"
                      onClick={() => void removeFromCatalog(l)}
                      variant="contextual"
                      size="xs"
                      className="opacity-0 group-hover/lbl:opacity-100 hover:text-error"
                    >
                      <CloseOutlinedIcon className="icon-xs" />
                    </TooltipButton>
                  )}
                </div>
              );
            })
          )}

          {canManageCatalog && (
            <div className="flex items-center gap-1 mt-1 pt-1 border-t border-border">
              <TextInput
                value={newName}
                onChange={setNewName}
                placeholder="New label…"
                ariaLabel="New label"
                maxLength={MAX_LABEL_NAME_LEN}
                className="input-custom-sm flex-1 min-w-0"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void create();
                  }
                }}
              />
              <TooltipButton
                title="Create label"
                onClick={() => void create()}
                disabled={!newName.trim() || busy}
                variant="contextual"
                size="sm"
                className="shrink-0"
              >
                <AddOutlinedIcon className="icon-xs" />
              </TooltipButton>
            </div>
          )}
        </div>
      </DropdownMenu>
    </>
  );
};
